import { EnvironmentConfig } from "@me8eon/config";

const parseLinks = (str: string): string => {
    // Normalize escaped < and >
    if (!str) return "";
    const normalized = str
        .replace(/\\u003c/g, "<")
        .replace(/\\u003e/g, ">");

    // Step 1: Replace <url>...</url> with <a target="_blank" ...>link</a>
    let result = normalized.replace(/<url>(.*?)<\/url>/g, (match, p1) => {
        const url = p1.trim();
        const { url: finalUrl, urlText } = addAzureBlobTokenInUrl(url);
        return `<a target="_blank" href="${finalUrl}" style="color: var(--link-color)">(${urlText})</a>`;
    });

    // Step 2: Replace markdown-style links [text](url)
    result = result.replace(/\[(.*?)\]\((https?:\/\/[^\)]+)\)/g, (match, p1, p2) => {
        const urlText = p1;
        let url = p2.trim();
        if (url.startsWith(EnvironmentConfig.azureBlobUrl as string)) {
            const hasToken = url.includes("?") && url.split("?")[1].includes("sig=");
            if (!hasToken) {
                const symbol = url.includes("?") ? "&" : "?";
                url = `${url}${symbol}${process.env.REACT_APP_AZURE_BLOB_ACCESS_KEY}`;
            }
        }
        return `<a target="_blank" href="${url}" style="color: var(--link-color)">${urlText}</a>`;
    });

    // Step 3: Replace bare URLs (https?://...) - more aggressive matching
    result = result.replace(/(https?:\/\/[^\s<>"']+)/g, (match, p1) => {
        // Clean up URL - remove trailing punctuation like . , : ;
        let url = p1.replace(/[.,;:!?)]*$/, '');

        // Don't convert if already inside an href or anchor tag
        const hrefIndex = result.lastIndexOf(`href="`, result.indexOf(match));
        const tagEndIndex = result.lastIndexOf(">", result.indexOf(match));
        if (hrefIndex > tagEndIndex) {
            return match;
        }

        const { url: finalUrl, urlText } = addAzureBlobTokenInUrl(url);
        return `<a target="_blank" href="${finalUrl}" style="color: var(--link-color); text-decoration: underline;" rel="noopener noreferrer">${urlText}</a>`;
    });

    return result;
};

const parseLinksOld = (str: string) => {
    // Step 0: Normalize escaped < and > (from React/HTML escaping)
    const normalized = str
        .replace(/\\u003c/g, "<")
        .replace(/\\u003e/g, ">");

    // Step 1: Escape all code sections (inline code and multi-line code blocks)
    const escapedBackticks = normalized
        // Handle inline code wrapped in backticks (e.g., `code`)
        .replace(/`([^`]+)`/g, (match) => {
            return `{{BACKTICK_SECTION_${Math.random().toString(36).substr(2, 9)}}}`;
        })
        // Handle multi-line code blocks wrapped in triple backticks (e.g., ``` code ```)
        .replace(/```([\s\S]*?)```/g, (match) => {
            return `{{BACKTICK_BLOCK_${Math.random().toString(36).substr(2, 9)}}}`;
        });

    // Step 1.1: If already coming within anchor tags, and is an azureblob link, then add the azure blob token
    const withAzureBlobToken = escapedBackticks.replace(
        /href="(.*?)"/g,
        (match, p1) => {
            let url = p1;
            if (url.startsWith(EnvironmentConfig.azureBlobUrl as string)) {
                const hasToken = url.includes("?") && url.split("?")[1].includes("sig=");
                if (hasToken) return match; // Already has token
                url = `${url}?${process.env.REACT_APP_AZURE_BLOB_ACCESS_KEY}`;
                return `href="${url}"`;
            }
            return match;
        }
    );

    // Step 1.2: Handle <url>...</url> tags
    const withUrlTags = withAzureBlobToken.replace(
        /<url>(.*?)<\/url>/g,
        (match, p1) => {
            return generateLink(p1);
        }
    );

    // Step 2: Replace markdown-style links with HTML <a> tags
    const withHtmlLinks = withUrlTags.replace(
        /\[(.*?)\]\((.*?)\)/g,
        (match, p1, p2) => {
            const urlText = p1.startsWith("https:") ? "link" : p1;
            if (p2.startsWith(EnvironmentConfig.azureBlobUrl as string)) {
                p2 = `${p2}?${process.env.REACT_APP_AZURE_BLOB_ACCESS_KEY}`;
            }
            return `<a target="_blank" href="${p2}" data-original-text="${urlText}">(${urlText}) </a>`;
        }
    );

    // Step 2.1: Handle [[https://...]] style links
    const withCustomLinks = withHtmlLinks.replace(
        /\[\[(https?:\/\/[^\s]+?)\]\]/g,
        (match, p1) => {
            return generateLink(p1);
        }
    );

    // Step 2.2: Replace bare URLs with HTML <a> tags
    const withParsedLinks = withCustomLinks.replace(/(https?:\/\/[^\s]+)/g, (match, p1) => {
        if (
            /href="/.test(
                withCustomLinks.slice(
                    withCustomLinks.indexOf(match) - 10,
                    withCustomLinks.indexOf(match) + 10
                )
            )
        ) {
            return match; // Already inside href
        }
        return generateLink(p1);
    });

    // Step 3: Reinsert the backtick sections
    return withParsedLinks
        .replace(/{{BACKTICK_SECTION_[a-z0-9]+}}/g, (match) => {
            const originalMatch = str.match(/`([^`]+)`/);
            return originalMatch ? originalMatch[0] : match;
        })
        .replace(/{{BACKTICK_BLOCK_[a-z0-9]+}}/g, (match) => {
            const originalMatch = str.match(/```([\s\S]*?)```/);
            return originalMatch ? originalMatch[0] : match;
        });
};

function addAzureBlobTokenInUrl(url: string, urlText?: string) {
    if (url.startsWith(EnvironmentConfig.azureBlobUrl as string)) {
        const hasToken = url.includes("?") && url.split("?")[1].includes("sig=");
        if (!hasToken) {
            const symbol = url.includes("?") ? "&" : "?";
            url = `${url}${symbol}${process.env.REACT_APP_AZURE_BLOB_ACCESS_KEY}`;
            if (!urlText) urlText = extractDocumentName(url);
        }
    }
    if (!urlText) urlText = url;
    return { url, urlText };
}

function generateLink(p1: string): string {
    const urlWithoutParams: string = p1.split(/[?#]/)[0];

    if (isPdfLink(urlWithoutParams)) {
        const pdfName = extractDocumentName(urlWithoutParams);
        if (
            urlWithoutParams.startsWith(
                EnvironmentConfig.genAiApiBaseUrl as string
            )
        ) {
            return `<a target="_blank" href="${p1}" style="display:none" data-original-text="${pdfName}">(${pdfName}) </a>`;
        }
        return `<a target="_blank" href="${p1}" data-original-text="${pdfName}">(${pdfName}) </a>`;
    }
    return `<a target="_blank" href="${p1}" data-original-text="link">(${p1}) </a>`;
}

function isPdfLink(url: string): boolean {
    return url.endsWith(".pdf");
}

function extractDocumentName(url: string): string {
    const urlWithoutParams: string = url.split(/[?#]/)[0];
    const docName = urlWithoutParams.split("/").pop()?.split("__").pop();
    return decodeURIComponent(docName || "Document");
}

export default parseLinks;

export const parsedLinkArray = (text: string) => {
    const regex = /href="(.*?)"(?:.*?data-original-text="(.*?)")?/g;
    let matches;
    const links = [];
    while ((matches = regex.exec(text)) !== null) {
        const linkObj = {
            url: matches[1],
            text: matches[2] || null,
        };
        links.push(linkObj);
    }
    return links;
};
